import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  isLiveUpdatesRequired,
  useActivateRecoveryMutation,
} from 'api/recoveryAi'
import type { BillingMethod } from 'api/recoveryAi.types'
import ModalContainer from 'components/ModalContainer'
import { buildTranslate } from 'locales'
import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'react-router-redux'
import { selectCurrentMerchant } from 'selectors/merchant'
import { useStyletron } from 'styletron-react'
import RadioOptionCard from '../components/RadioOptionCard'
import StatusPill from '../components/StatusPill'
import { ACTIVATION_SUCCESS_PATH } from '../routes'
import ActivationAgreement from './ActivationAgreement'
import {
  ESCALATION_CENTS,
  PER_RECOVERY_CENTS,
  PER_RESOLUTION_CENTS,
} from './constants'
import CreditPanel from './CreditPanel'
import { formatCentsUsd } from './format'
import RateCard from './RateCard'

const t = buildTranslate('recovery_ai.activation.pricing_modal')

// The design's content column is materially wider than ModalContainer's 600px
// default — the agreement paragraph and the 2-up rate cards both need it.
const DIALOG_WIDTH = '800px'
const CARD_GAP = '16px'
const RADIO_GAP = '12px'

// Stable ids linking each radio to its helper copy via aria-describedby. Module
// constants are safe: at most one pricing modal mounts at a time.
const CENTRAL_HELP_ID = 'activation-billing-central-help'
const PER_LOCATION_HELP_ID = 'activation-billing-per-location-help'

interface Props {
  isOpen: boolean
  onClose: () => void
  // Gate the central-vs-per-location billing radio. Defaults to FALSE (AI-1133): the
  // backend can't yet distinguish which merchants may bill centrally, so the interim
  // fix hides the choice and always bills per-location (the initial billingMethod).
  // The radio block is retained behind this flag for a one-line re-enable once the
  // merchant-config read exists.
  showBillingChoice?: boolean
  // Fired when the PUT refuses with `live_updates_required`: stale client
  // state, or another admin turned Live Updates off.
  onLiveUpdatesRequired?: () => void
}

// AI-169 pricing/activation modal. Built on the Base Web ModalContainer (focus
// trap / ESC / dialog aria / disable-while-loading proceed). "activate" fires the
// AUDITED activation PUT that runs make-good provisioning on the off→on
// transition (MONEY-SAFETY — see useActivateRecoveryMutation). Billing backend
// is still out of scope (billingMethod is not sent).
const ActivationPricingModal = ({
  isOpen,
  onClose,
  showBillingChoice = false,
  onLiveUpdatesRequired,
}: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const merchant = useSelector(selectCurrentMerchant)
  // Default per-location (AI-1133): the choice is hidden by default (showBillingChoice)
  // and the backend has no billing field yet, so this is the value that flows through.
  const [billingMethod, setBillingMethod] =
    useState<BillingMethod>('per_location')
  const [activate, { isLoading, isError, error, reset }] =
    useActivateRecoveryMutation()
  // RTK's `reset` closes over the in-flight request, so the copy captured when
  // the CTA is clicked (before the request exists) is a no-op. The settle
  // handlers below run later, so they read the latest render's copy.
  const resetRef = useRef(reset)
  resetRef.current = reset
  const isHandedOff = !!onLiveUpdatesRequired && isLiveUpdatesRequired(error)

  const handleClose = () => {
    // The in-flight PUT can't be aborted client-side; lock the close path until it
    // settles so handleActivate's .then doesn't double-fire reset()/onClose().
    if (isLoading) return
    // Clear a failed-activate error + the billing choice so a stale banner/selection
    // doesn't carry into the next open — ConfiguredSummary keeps this modal mounted
    // (toggling isOpen) for the duration of the admin's session.
    reset()
    setBillingMethod('per_location')
    onClose()
  }

  const handleActivate = () => {
    activate({ billingMethod })
      .unwrap()
      .then(() => {
        // Reset the mutation + billing state before navigating so nothing stale
        // survives if this modal is ever kept mounted across the transition.
        // (Today it unmounts with ConfiguredSummary on the route change, but the
        // success path shouldn't depend on that unmount to stay clean.)
        resetRef.current()
        setBillingMethod('per_location')
        // Hand billingMethod off via router state — the interim activate mutation
        // doesn't persist/return it (AI-1133, no billing backend yet). Timestamp
        // and authorizer no longer ride this hand-off (AI-1008): the success page
        // reads both from GET recovery/configuration/activation, the authoritative
        // activation event, which survives refresh and isn't the current viewer.
        dispatch(
          push({
            pathname: ACTIVATION_SUCCESS_PATH,
            state: { billingMethod },
          })
        )
      })
      // Failure is surfaced inline via isError below; swallow so it isn't an
      // unhandled rejection.
      .catch(failure => {
        if (onLiveUpdatesRequired && isLiveUpdatesRequired(failure)) {
          resetRef.current()
          setBillingMethod('per_location')
          onLiveUpdatesRequired()
        }
      })
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={t('title')}
      width={DIALOG_WIDTH}
      // Own the footer: the design puts "Go back" on the LEFT and the primary on the
      // RIGHT, and ModalContainer's built-in row is justify-content-end. With
      // hideCancel + showBottomProceed={false} and no buttonGroupChildren its whole
      // footer Switch evaluates false. X / ESC / backdrop still route through
      // onClose, so handleClose's in-flight guard still protects the PUT.
      hideCancel
      showBottomProceed={false}
    >
      <StatusPill tone="ready">{t('ready_pill')}</StatusPill>
      <Text.Header3 tag="div" color="grey90" className="mt-s">
        {t('title')}
      </Text.Header3>
      <Text.BodyText4 tag="div" color="grey70" className="mt-xs">
        {t('subtitle')}
      </Text.BodyText4>

      {/* Same type size as the title by design — do not step this down a level. */}
      <Text.Header3 tag="div" className="mt-l">
        {t('headline')}
      </Text.Header3>

      <Text.BodyText4 tag="div" bold color="grey90" className="mt-l">
        {t('rates_label')}
      </Text.BodyText4>
      <div className={`d-flex ${css({ gap: CARD_GAP, marginTop: '8px' })}`}>
        <RateCard
          label={t('escalation_title')}
          amount={formatCentsUsd(ESCALATION_CENTS)}
          descriptor={t('escalation_label')}
        />
        <RateCard
          label={t('resolution_title')}
          amount={formatCentsUsd(PER_RESOLUTION_CENTS, { cents: true })}
          descriptor={t('resolution_label')}
        />
        <RateCard
          label={t('recovery_title')}
          amount={formatCentsUsd(PER_RECOVERY_CENTS, { cents: true })}
          descriptor={t('recovery_label')}
        />
      </div>

      <div className="mt-l">
        <CreditPanel />
      </div>

      {showBillingChoice && (
        <div className="mt-l">
          {/* Label + helper live INSIDE the guard: with no choice rendered, a
              heading would sit over nothing. */}
          <Text.BodyText4 tag="div" bold color="grey90">
            {t('billing.section_label')}
          </Text.BodyText4>
          <Text.BodyText4 tag="div" color="grey70">
            {t('billing.section_help')}
          </Text.BodyText4>
          <div
            className={`d-flex flex-column ${css({
              gap: RADIO_GAP,
              marginTop: '12px',
            })}`}
          >
            <RadioOptionCard
              selected={billingMethod === 'central'}
              value="central"
              name="activation-billing"
              ariaLabel={t('billing.central')}
              ariaDescribedBy={CENTRAL_HELP_ID}
              onChange={() => setBillingMethod('central')}
            >
              <Text.BodyText4 bold>{t('billing.central')}</Text.BodyText4>
              {/* id'd span because uikit Text takes no id — the radio's name comes
                  from ariaLabel, so this helper is otherwise invisible to AT. */}
              <span id={CENTRAL_HELP_ID}>
                <Text.BodyText4 color="grey70">
                  {t('billing.central_help')}
                </Text.BodyText4>
              </span>
            </RadioOptionCard>
            <RadioOptionCard
              selected={billingMethod === 'per_location'}
              value="per_location"
              name="activation-billing"
              ariaLabel={t('billing.per_location')}
              ariaDescribedBy={PER_LOCATION_HELP_ID}
              onChange={() => setBillingMethod('per_location')}
            >
              <Text.BodyText4 bold>{t('billing.per_location')}</Text.BodyText4>
              <span id={PER_LOCATION_HELP_ID}>
                <Text.BodyText4 color="grey70">
                  {t('billing.per_location_help')}
                </Text.BodyText4>
              </span>
            </RadioOptionCard>
          </div>
        </div>
      )}

      <div className="mt-l">
        <ActivationAgreement merchantName={merchant?.name} />
      </div>

      {isError && !isHandedOff && (
        <div className={css({ marginTop: '12px' })}>
          <Notification kind="negative" size="full" closeable onClose={reset}>
            {t('submit_error')}
          </Notification>
        </div>
      )}

      <div className="mt-xl d-flex justify-content-between align-items-center">
        {/* A blue text link in the design. Left enabled while loading, matching the
            Cancel it replaces — handleClose's guard makes it inert mid-flight. */}
        <Button kind="minimal" className="pl-0" onClick={handleClose}>
          {t('go_back')}
        </Button>
        <Button
          kind="primary"
          onClick={handleActivate}
          disabled={isLoading}
          isLoading={isLoading}
        >
          {t('agree_and_activate')}
        </Button>
      </div>
    </ModalContainer>
  )
}

export default ActivationPricingModal
